import { format } from '@pf/money';
import { useParams } from '@tanstack/react-router';
import {
  BedDouble,
  Bus,
  Car,
  FileText,
  Paperclip,
  Pencil,
  Plane,
  Ship,
  Ticket,
  TrainFront,
  Utensils,
} from 'lucide-react';
import { useRef, useState } from 'react';
import f from '../app/Dialog.module.css';
import { Dialog, Field } from '../app/Dialog.tsx';
import { useMe, useTrip } from '../app/data.ts';
import { cx } from '../kit/cx.ts';
import { Button, DataTag } from '../kit/index.ts';
import t from '../kit/type.module.css';
import { attach } from '../model/attachments.ts';
import { flag, type Reservation, status, type TripModel, time } from '../model/trip.ts';
import { duration, instant, zoneLabel } from '../model/zones.ts';
import type { Edit } from '../sync.ts';
import s from './Bookings.module.css';

const ICONS = {
  flight: Plane,
  lodging: BedDouble,
  train: TrainFront,
  bus: Bus,
  ferry: Ship,
  car: Car,
  restaurant: Utensils,
} as const;
const icon = (kind: string) => ICONS[kind as keyof typeof ICONS] ?? Ticket;
const JOURNEY = new Set(['flight', 'train', 'bus', 'ferry']);

/** Per-kind fields, as the apps' BookingSheet. */
const FIELDS: Record<string, [string, string][]> = {
  flight: [
    ['airlineCode', 'Airline code'],
    ['number', 'Flight number'],
    ['airline', 'Airline'],
    ['from', 'From (IATA)'],
    ['fromCity', 'From city'],
    ['to', 'To (IATA)'],
    ['toCity', 'To city'],
    ['terminal', 'Terminal'],
    ['gate', 'Gate'],
    ['seat', 'Seat'],
    ['boarding', 'Boarding time'],
  ],
  lodging: [
    ['address', 'Address'],
    ['phone', 'Phone'],
  ],
  train: [
    ['carrier', 'Carrier'],
    ['number', 'Train'],
    ['from', 'From'],
    ['to', 'To'],
    ['coach', 'Coach'],
    ['seat', 'Seat'],
    ['platform', 'Platform'],
  ],
  bus: [
    ['carrier', 'Carrier'],
    ['from', 'From'],
    ['to', 'To'],
    ['seat', 'Seat'],
  ],
  ferry: [
    ['carrier', 'Carrier'],
    ['from', 'From'],
    ['to', 'To'],
  ],
  car: [
    ['company', 'Company'],
    ['pickUp', 'Pick-up'],
    ['dropOff', 'Drop-off'],
  ],
  restaurant: [
    ['party', 'Party size'],
    ['reference', 'Reference'],
  ],
  activity: [
    ['party', 'Party size'],
    ['reference', 'Reference'],
  ],
};

/** F-04 bookings: every reservation in time order, with its codes, times, money and files. */
export function Bookings() {
  const { tripId } = useParams({ from: '/app/trips/$tripId/bookings' });
  const { model, save, error } = useTrip(tripId);
  const me = useMe();
  const [editing, setEditing] = useState<Reservation | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  if (error)
    return (
      <p className={cx(s.pad, t.bodyS)} role="alert">
        {error.message}
      </p>
    );
  if (!model) return <p className={cx(s.pad, t.bodyS)}>Loading…</p>;
  const run = async (edits: Edit[]) => {
    const refused = await save(edits);
    setNotice(refused > 0 ? "A change wasn't saved; the screen shows what the server has." : null);
  };
  const files = model.attachments.filter((a) => a.ownerType === 'item');
  const missing = model.reservations.filter((r) => !files.some((a) => a.ownerId === r.itemId)).length;
  return (
    <div className={s.page}>
      <header className={s.top}>
        <div className={cx(t.readoutS, s.muted)}>
          {model.title} · {flag(model.startDate)} ▶ {flag(model.endDate)}
        </div>
        <h1 className={cx(t.displayL, s.title)}>Bookings</h1>
        <div className={cx(t.readoutS, s.muted)}>
          {model.reservations.length} {missing > 0 ? `· ${missing} without a file` : ''}
        </div>
      </header>
      {notice && (
        <p className={cx(s.notice, t.bodyS)} role="status">
          {notice}
        </p>
      )}
      {model.reservations.length === 0 && (
        <p className={cx(s.pad, t.bodyS, s.muted)}>
          No bookings yet. They come from Wanderlog imports, or from the apps.
        </p>
      )}
      <ol className={s.cards}>
        {model.reservations.map((r) => (
          <Card
            key={r.id}
            r={r}
            model={model}
            me={me.data?.user.id ?? null}
            onEdit={() => setEditing(r)}
            run={run}
          />
        ))}
      </ol>
      {editing && (
        <EditBooking
          r={editing}
          tripId={tripId}
          onClose={() => setEditing(null)}
          onSave={async (edit) => {
            await run([edit]);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function Card({
  r,
  model,
  me,
  onEdit,
  run,
}: {
  r: Reservation;
  model: TripModel;
  me: string | null;
  onEdit: () => void;
  run: (e: Edit[]) => Promise<void>;
}) {
  const item =
    model.days.flatMap((d) => d.items).find((p) => p.item.id === r.itemId)?.item ??
    model.lists.flatMap((l) => l.items).find((p) => p.item.id === r.itemId)?.item;
  const Icon = icon(r.kind);
  const start = instant(r.startLocal, r.startTz);
  const end = instant(r.endLocal, r.endTz ?? r.startTz);
  const d = r.details;
  const [copied, setCopied] = useState(false);
  const [uploading, setUploading] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const expenses = model.entries.filter((e) => e.itemId && e.itemId === r.itemId);
  const files = model.attachments.filter((a) => a.ownerType === 'item' && a.ownerId === r.itemId);
  const tags: [string, string | undefined][] =
    r.kind === 'flight'
      ? [
          ['Gate', d.gate],
          ['Seat', d.seat],
          ['Board', d.boarding],
          ['Term', d.terminal],
        ]
      : JOURNEY.has(r.kind)
        ? [
            ['Coach', d.coach],
            ['Seat', d.seat],
            ['Platform', d.platform],
          ]
        : r.kind === 'lodging'
          ? [['Phone', d.phone]]
          : [
              ['Party', d.party],
              ['Ref', d.reference],
            ];

  const copy = async () => {
    if (!r.confirmation) return;
    await navigator.clipboard.writeText(r.confirmation).catch(() => undefined);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  const attachFile = async (file: File) => {
    if (!r.itemId) return;
    setUploading(true);
    try {
      await attach(run, { tripId: model.id, ownerType: 'item', ownerId: r.itemId, me }, file);
    } finally {
      setUploading(false);
    }
  };

  return (
    <li className={s.card}>
      <div className={s.kind}>
        <Icon size={20} aria-hidden="true" />
        <span className={t.label}>{r.kind}</span>
      </div>
      <div className={s.body}>
        <div className={s.head}>
          <h2 className={cx(t.titleL, s.name)}>{item?.title ?? 'Booking'}</h2>
          {r.confirmation && (
            <button
              type="button"
              className={s.conf}
              onClick={() => void copy()}
              aria-label={`Copy confirmation code ${r.confirmation}`}
            >
              <DataTag label={copied ? 'Copied' : 'Conf'} value={r.confirmation} spoken="Confirmation code" />
            </button>
          )}
          <button type="button" className={s.icon} onClick={onEdit} aria-label="Edit booking">
            <Pencil size={16} />
          </button>
        </div>
        {JOURNEY.has(r.kind) ? (
          <div className={s.journey}>
            <End code={d.from} city={d.fromCity} local={r.startLocal} tz={r.startTz} at={start} />
            <div className={cx(s.line, t.readoutS, s.muted)}>
              {[
                d.airlineCode && d.number ? `${d.airlineCode} ${d.number}` : d.number,
                start !== null && end !== null && end > start
                  ? duration(Math.round((end - start) / 60000))
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
            <End code={d.to} city={d.toCity} local={r.endLocal} tz={r.endTz ?? r.startTz} at={end} right />
          </div>
        ) : (
          <div className={cx(s.when, t.readoutS)}>
            {r.kind === 'lodging' ? 'In ' : ''}
            {flag(r.startLocal)} {time(r.startLocal) ?? ''}
            {r.endLocal && (
              <>
                {' '}
                → {r.kind === 'lodging' ? 'out ' : ''}
                {flag(r.endLocal)} {time(r.endLocal) ?? ''}
              </>
            )}
            {r.kind === 'lodging' &&
              start !== null &&
              end !== null &&
              ` · ${Math.round((end - start) / 86_400_000)} N`}
            <span className={s.muted}> {zoneLabel(r.startTz, start)}</span>
          </div>
        )}
        {(d.address || item?.place?.address) && (
          <div className={cx(t.caption, s.muted)}>{d.address ?? item?.place?.address}</div>
        )}
        <div className={s.tags}>
          {tags
            .filter((x): x is [string, string] => Boolean(x[1]))
            .map(([k, v]) => (
              <DataTag key={k} label={k} value={v} />
            ))}
          {expenses.map((e) => (
            <DataTag
              key={e.id}
              label={status(e) === 'paid' ? 'Paid' : status(e) === 'due' ? 'Due' : 'Planned'}
              value={format(e.amountMinor, e.currency)}
              tone={status(e) === 'due' ? 'hazard' : 'standard'}
            />
          ))}
        </div>
        <div className={s.files}>
          {files.map((a) => (
            <a
              key={a.id}
              href={`/v1/attachments/${a.id}`}
              target="_blank"
              rel="noreferrer"
              className={cx(s.file, t.caption)}
            >
              <FileText size={14} aria-hidden="true" /> {a.filename}
              {!a.uploadedAt && <span className={s.muted}> · uploading on another device</span>}
            </a>
          ))}
          {r.itemId && (
            <>
              <input
                ref={input}
                type="file"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void attachFile(file);
                  e.target.value = '';
                }}
              />
              <Button
                variant="ghost"
                size="small"
                icon={Paperclip}
                disabled={uploading}
                onClick={() => input.current?.click()}
              >
                {uploading ? 'Uploading…' : 'Attach'}
              </Button>
            </>
          )}
        </div>
      </div>
    </li>
  );
}

function End({
  code,
  city,
  local,
  tz,
  at,
  right = false,
}: {
  code?: string | undefined;
  city?: string | undefined;
  local: string | null;
  tz: string | null;
  at: number | null;
  right?: boolean;
}) {
  return (
    <div className={cx(s.end, right && s.right)}>
      <div className={t.readoutL}>{code ?? '—'}</div>
      <div className={cx(t.caption, s.muted)}>{city ?? ''}</div>
      <div className={t.readoutM}>
        {time(local) ?? '--:--'} <span className={cx(t.readoutS, s.muted)}>{zoneLabel(tz, at)}</span>
      </div>
      <div className={cx(t.readoutS, s.muted)}>{flag(local)}</div>
    </div>
  );
}

function EditBooking({
  r,
  tripId,
  onClose,
  onSave,
}: {
  r: Reservation;
  tripId: string;
  onClose: () => void;
  onSave: (e: Edit) => Promise<void>;
}) {
  const [kind, setKind] = useState(r.kind);
  const [confirmation, setConfirmation] = useState(r.confirmation ?? '');
  const [startLocal, setStart] = useState(r.startLocal ?? '');
  const [startTz, setStartTz] = useState(r.startTz ?? '');
  const [endLocal, setEnd] = useState(r.endLocal ?? '');
  const [endTz, setEndTz] = useState(r.endTz ?? '');
  const [details, setDetails] = useState<Record<string, string>>(r.details);
  const zones = Intl.supportedValuesOf?.('timeZone') ?? [];
  const submit = () => {
    const kept = Object.fromEntries(
      Object.entries(details)
        .filter(([, v]) => v.trim() !== '')
        .sort(([a], [b]) => a.localeCompare(b)),
    );
    void onSave({
      table: 'reservation',
      id: r.id,
      tripId,
      fields: {
        kind,
        confirmation: confirmation.trim() || null,
        startLocal: startLocal || null,
        startTz: startTz || null,
        endLocal: endLocal || null,
        endTz: endTz || null,
        detailsJson: JSON.stringify(kept),
      },
    });
  };
  return (
    <Dialog title="Edit booking" onClose={onClose} onSubmit={submit}>
      <div className={f.pair}>
        <Field label="Kind">
          <select value={kind} onChange={(e) => setKind(e.target.value)} className={f.input}>
            {['flight', 'lodging', 'train', 'bus', 'ferry', 'car', 'restaurant', 'activity', 'other'].map(
              (k) => (
                <option key={k}>{k}</option>
              ),
            )}
          </select>
        </Field>
        <Field label="Confirmation">
          <input
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            className={cx(f.input, t.readoutM)}
          />
        </Field>
      </div>
      <datalist id="pf-zones">
        {zones.map((z) => (
          <option key={z} value={z} />
        ))}
      </datalist>
      <div className={f.pair}>
        <Field label="Starts (local)">
          <input
            type="datetime-local"
            value={startLocal}
            onChange={(e) => setStart(e.target.value)}
            className={f.input}
          />
        </Field>
        <Field label="Zone">
          <input
            list="pf-zones"
            value={startTz}
            onChange={(e) => setStartTz(e.target.value)}
            className={f.input}
            placeholder="Asia/Tokyo"
          />
        </Field>
      </div>
      <div className={f.pair}>
        <Field label="Ends (local)">
          <input
            type="datetime-local"
            value={endLocal}
            onChange={(e) => setEnd(e.target.value)}
            className={f.input}
          />
        </Field>
        <Field label="Zone">
          <input
            list="pf-zones"
            value={endTz}
            onChange={(e) => setEndTz(e.target.value)}
            className={f.input}
            placeholder="Same as start"
          />
        </Field>
      </div>
      <div className={s.detailGrid}>
        {(FIELDS[kind] ?? []).map(([key, label]) => (
          <Field key={key} label={label}>
            <input
              value={details[key] ?? ''}
              onChange={(e) => setDetails({ ...details, [key]: e.target.value })}
              className={f.input}
            />
          </Field>
        ))}
      </div>
      <div className={f.actions}>
        <span className={f.spacer} />
        <Button variant="ghost" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" type="submit">
          Save
        </Button>
      </div>
    </Dialog>
  );
}
